// BranchStockTabs.js — replaces the single "how long each shelf lasts" bar
// list for a selected branch with four tabs:
//
//   1. Excess/under stocked items — spare stock at THIS branch, who could use
//      it, adjustable by two sliders (months to leave behind, minimum
//      transfer value).
//   2. Drugs found in stock but no usage figures/usage figures zero.
//   3. Drugs not found in stock, but have usage data.
//   4. Top 200 lines over the last three months — this branch's own top
//      dispensed lines, by value or by packet count.
//
// All the arithmetic is estate_model.js's computeBalance, called again here
// at whatever "months to leave" the person has set on the slider - it is the
// same pure function the rest of the screen uses, just re-run at a different
// target so this tab does not have to agree with the global "Keep enough
// stock for" slider above it.

import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { displayName } from "./nameMask";
import { computeBalance, monthsLabel } from "./estate_model";

const money = (n) =>
  `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CONF_LABEL = { high: "steady", medium: "fair", low: "unsure", none: "no usage" };
const CONF_COLOUR = { high: "#639922", medium: "#BA7517", low: "#888780", none: "#888780" };

const th = { textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: "6px 8px", whiteSpace: "nowrap" };
const td = { borderBottom: "1px solid #f1f5f9", padding: "6px 8px", verticalAlign: "top" };

/* ---------------------------------------------------------------------------
   Small helpers
   --------------------------------------------------------------------------- */

// Re-runs the one shared balance calculation at a locally chosen months
// target, without touching the monthsAhead the rest of the screen is set to.
function recompute(row, monthsAhead) {
  const b = computeBalance({
    qtyOnShelf: row.qtyOnShelf,
    pendingOut: row.pendingOut,
    pendingIn: row.pendingIn,
    usage: row.usage,
    monthly: row.monthly,
    packSize: row.packSize,
    monthsAhead,
  });
  return {
    ...row,
    ...b,
    certainExcessPacks: row.usable ? b.certainExcessPacks : 0,
    unsureExcessPacks: row.usable ? b.unsureExcessPacks : b.excessPacks,
  };
}

function sortBy(list, sort, accessors) {
  const fn = accessors[sort.key] || Object.values(accessors)[0];
  const copy = [...list];
  copy.sort((a, b) => {
    const va = fn(a), vb = fn(b);
    let cmp;
    if (typeof va === "string" || typeof vb === "string") cmp = String(va).localeCompare(String(vb));
    else cmp = (va ?? -Infinity) - (vb ?? -Infinity);
    return sort.dir === "asc" ? cmp : -cmp;
  });
  return copy;
}

function SortTh({ label, colKey, sort, setSort, align, title }) {
  const active = sort.key === colKey;
  return (
    <th
      style={{ ...th, cursor: "pointer", userSelect: "none", textAlign: align || "left" }}
      title={title}
      onClick={() => setSort((s) => (s.key === colKey ? { key: colKey, dir: s.dir === "asc" ? "desc" : "asc" } : { key: colKey, dir: "desc" }))}
    >
      {label}{active ? (sort.dir === "asc" ? " \u25b2" : " \u25bc") : ""}
    </th>
  );
}

function exportRows(filename, sheetName, pharmacyName, columns, rows) {
  const aoa = [];
  aoa.push([pharmacyName || ""]);
  aoa.push([new Date().toLocaleDateString("en-GB")]);
  aoa.push([]);
  aoa.push(columns.map((c) => c.label));
  rows.forEach((r) => aoa.push(columns.map((c) => c.value(r))));
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// "In stock, no usage" goes through ExcelJS rather than the plain xlsx writer
// above - centred number columns and a real dd/mm/yyyy date format on the
// expiry column both need actual cell styles, which the xlsx package here
// cannot write. The Drug column width is sized to the longest name in the
// data rather than a fixed guess, so nothing gets truncated on open.
async function exportNoUsageRows(pharmacyName, rows) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("In stock no usage");

  ws.addRow([pharmacyName || ""]);
  ws.addRow([new Date().toLocaleDateString("en-GB")]);
  ws.addRow([]);
  const headerRow = ws.addRow(["Drug", "Current packs", "Value", "Expiry date"]);
  headerRow.eachCell((cell) => { cell.font = { bold: true }; });

  let longestName = "Drug".length;

  rows.forEach((r) => {
    const raw = r.items && r.items[0] ? r.items[0].expiryDate : null;
    const expiry = raw ? new Date(raw) : null;
    const validExpiry = expiry && !isNaN(expiry.getTime()) ? expiry : null;

    const row = ws.addRow([
      r.drugName || "",
      Math.round(r.packetsOnShelf || 0),
      Number((r.totalValue || 0).toFixed(2)),
      validExpiry,
    ]);

    if (r.drugName && r.drugName.length > longestName) longestName = r.drugName.length;

    row.getCell(2).alignment = { horizontal: "center" };
    row.getCell(3).alignment = { horizontal: "center" };
    row.getCell(3).numFmt = "£#,##0.00";
    if (validExpiry) row.getCell(4).numFmt = "dd/mm/yyyy";
  });

  ws.getColumn(1).width = longestName + 4;   // Drug - fits the longest name
  ws.getColumn(2).width = 16;                // Current packs
  ws.getColumn(3).width = 14;                // Value
  ws.getColumn(4).width = 14;                // Expiry date

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `in-stock-no-usage-${pharmacyName || "branch"}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
}

// The three-month usage picture: a bar per month, the pack count (rounded up)
// underneath it, and the confidence verdict below that - the evidence for the
// word, not just the word.
function UsageDetail({ monthly = [], packSize = 1, confidence }) {
  const months = [...monthly].sort((a, b) => String(a.month).localeCompare(String(b.month)));
  if (!months.length) return <span style={{ fontSize: "0.78em", color: "#94a3b8" }}>no months</span>;
  const peak = Math.max(...months.map((m) => m.qty || 0), 1);
  const label = (ym) => {
    const t = String(ym || "");
    if (t.length !== 6) return t;
    return new Date(Number(t.slice(0, 4)), Number(t.slice(4, 6)) - 1).toLocaleString("en-GB", { month: "short" });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 130 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", height: 40 }}>
        {months.map((m) => {
          const qty = m.qty || 0;
          const packs = Math.ceil((packSize > 0 ? qty / packSize : qty) - 1e-9);
          const h = qty > 0 ? Math.max(4, Math.round((qty / peak) * 34)) : 3;
          return (
            <div key={m.month} title={`${label(m.month)}: ${qty} unit${qty === 1 ? "" : "s"} (\u2248 ${packs} pk)`}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, width: 20 }}>
              <div style={{ width: 14, height: h, borderRadius: 2, background: qty > 0 ? "#64748b" : "#cbd5e1" }} />
              <div style={{ fontSize: "0.66em", color: "#94a3b8" }}>{label(m.month)}</div>
              <div style={{ fontSize: "0.7em", fontWeight: 600, color: "#334155" }}>{packs} pk</div>
            </div>
          );
        })}
      </div>
      {confidence && (
        <div style={{ fontSize: "0.76em", fontWeight: 700, color: CONF_COLOUR[confidence.level] }} title={confidence.why}>
          {CONF_LABEL[confidence.level]}
        </div>
      )}
    </div>
  );
}

function ItemsTable({ items = [] }) {
  if (!items.length) return <div style={{ color: "#64748b", fontSize: "0.85em", padding: "6px 10px" }}>Nothing on the shelf.</div>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8em" }}>
      <thead style={{ background: "#fbfcfd" }}>
        <tr>
          <th style={{ ...th, padding: "5px 8px" }}>Packets</th>
          <th style={{ ...th, padding: "5px 8px" }}>Pack size</th>
          <th style={{ ...th, padding: "5px 8px" }}>Batch</th>
          <th style={{ ...th, padding: "5px 8px" }}>Serial</th>
          <th style={{ ...th, padding: "5px 8px" }}>Expiry</th>
          <th style={{ ...th, padding: "5px 8px" }}>First scanned</th>
          <th style={{ ...th, padding: "5px 8px" }}>Location</th>
          <th style={{ ...th, padding: "5px 8px", textAlign: "right" }}>Value</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it, n) => (
          <tr key={n} style={{ background: it.split ? "#fdfaf3" : "#fff" }}>
            <td style={{ ...td, padding: "5px 8px" }}>
              {it.packets}
              {it.split && <span title="An opened box. Its quantity is what is left, not a pack size." style={{ color: "#a05a00" }}> · split</span>}
              {it.fromInvoices && <span title="Assumed from an incoming invoice — not counted on the shelf." style={{ color: "#64748b" }}> · assumed</span>}
            </td>
            <td style={{ ...td, padding: "5px 8px" }}>{it.packSize || "-"}</td>
            <td style={{ ...td, padding: "5px 8px" }}>{it.batchNumber || "—"}</td>
            <td style={{ ...td, padding: "5px 8px" }}>{it.serialNumber || "—"}</td>
            <td style={{ ...td, padding: "5px 8px" }}>{it.expiryDate ? String(it.expiryDate).slice(0, 10) : "-"}</td>
            <td style={{ ...td, padding: "5px 8px" }}>{it.firstScanned ? String(it.firstScanned).slice(0, 10) : "-"}</td>
            <td style={{ ...td, padding: "5px 8px" }}>{it.location || "-"}</td>
            <td style={{ ...td, padding: "5px 8px", textAlign: "right" }}>{money(it.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const TABS = [
  { id: "excess", label: "Excess stock items" },
  { id: "understocked", label: "Understocked items" },
  { id: "noUsage", label: "In stock, no usage" },
  { id: "noStock", label: "Usage, no stock" },
  { id: "top200", label: "Top 200 lines (3 full months)" },
];

// One drug, one panel. Every field for a row lives in this same flex row so
// nothing can render into a neighbour's space - the usage chart, the packs,
// the recipients list are all children of one bordered card, not cells in a
// table whose column widths a browser is free to renegotiate per row.
const EXCESS_COLS = [
  { key: "name", label: "Drug", flex: "1 1 210px", sortable: true },
  { key: "usage", label: "Usage over 3 months", flex: "0 0 190px", sortable: false },
  { key: "packs", label: "Current total packs", flex: "0 0 120px", align: "right", sortable: true },
  { key: "months", label: "Months held", flex: "0 0 100px", align: "right", sortable: true },
  { key: "transferPacks", label: "Packs available for transfer", flex: "0 0 150px", align: "right", sortable: true },
  { key: "transferValue", label: "Value of available transfer", flex: "0 0 150px", align: "right", sortable: true },
  { key: "recipients", label: "Possible recipients", flex: "1 1 230px", sortable: true },
];

// Same shape as EXCESS_COLS - same widths, same order - so the "Understocked"
// tab is visually identical to "Excess", just carrying deficit numbers and
// possible source branches instead of surplus numbers and recipients.
const UNDERSTOCKED_COLS = [
  { key: "name", label: "Drug", flex: "1 1 210px", sortable: true },
  { key: "usage", label: "Usage over 3 months", flex: "0 0 190px", sortable: false },
  { key: "packs", label: "Current total packs", flex: "0 0 120px", align: "right", sortable: true },
  { key: "months", label: "Months held", flex: "0 0 100px", align: "right", sortable: true },
  { key: "packsNeeded", label: "Packs needed", flex: "0 0 150px", align: "right", sortable: true },
  { key: "valueNeeded", label: "Value needed", flex: "0 0 150px", align: "right", sortable: true },
  { key: "sources", label: "Possible sources", flex: "1 1 230px", sortable: true },
];

/* ===========================================================================
   Screen
   =========================================================================== */
export default function BranchStockTabs({ branch, allRows, monthsAheadDefault, masked, usageWindow }) {
  const [tab, setTab] = useState("excess");
  const [monthsToLeave, setMonthsToLeave] = useState(monthsAheadDefault || 3);
  const [minValue, setMinValue] = useState(0);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const [sortExcess, setSortExcess] = useState({ key: "transferValue", dir: "desc" });
  const [sortUnderstocked, setSortUnderstocked] = useState({ key: "valueNeeded", dir: "desc" });
  const [sortNoUsage, setSortNoUsage] = useState({ key: "value", dir: "desc" });
  const [sortNoStock, setSortNoStock] = useState({ key: "usage", dir: "desc" });
  const [sortTop, setSortTop] = useState({ key: "metric", dir: "desc" });
  const [topBasis, setTopBasis] = useState("value"); // "value" | "packets"

  const pharmacyName = branch?.pharmacyName;

  const rowsAtMonths = useMemo(
    () => (branch?.rows || []).map((r) => recompute(r, monthsToLeave)),
    [branch, monthsToLeave]
  );

  const needle = search.trim().toLowerCase();
  const matches = (name) => !needle || String(name).toLowerCase().includes(needle);

  /* ---- tab 1: excess/under stocked ---- */
  const excessRows = useMemo(() => {
    return rowsAtMonths
      .filter((r) => r.qtyOnShelf > 0 && r.usage > 0 && r.excessPacks > 0 && matches(r.drugName))
      .map((r) => {
        const transferPacks = r.certainExcessPacks;
        const transferValue = transferPacks * r.packSize * r.unitValue;
        const recipients = (allRows || [])
          .filter((o) => o.cleanName === r.cleanName && o.pharmacyId !== r.pharmacyId && o.usable)
          .map((o) => recompute(o, monthsToLeave))
          .filter((o) => o.deficitPacks > 0)
          .map((o) => ({
            pharmacyName: o.pharmacyName,
            needed: Math.max(1, Math.ceil(o.deficitUnits / (r.packSize > 0 ? r.packSize : 1))),
          }))
          .sort((a, b) => b.needed - a.needed);
        return { row: r, transferPacks, transferValue, recipients };
      })
      .filter((x) => x.transferValue >= (Number(minValue) || 0));
  }, [rowsAtMonths, allRows, monthsToLeave, minValue, needle]); // eslint-disable-line react-hooks/exhaustive-deps

  const excessSorted = useMemo(() => sortBy(excessRows, sortExcess, {
    name: (x) => x.row.drugName,
    months: (x) => x.row.monthsCover ?? -1,
    packs: (x) => x.row.packetsOnShelf,
    transferPacks: (x) => x.transferPacks,
    transferValue: (x) => x.transferValue,
    recipients: (x) => x.recipients.length,
  }), [excessRows, sortExcess]);

  // What's actually on screen right now, after search/months/minValue have
  // all been applied - not the estate total, and not the pre-filter total.
  const totalDisplayedTransferValue = useMemo(
    () => excessSorted.reduce((t, x) => t + x.transferValue, 0),
    [excessSorted]
  );

  /* ---- tab 1b: understocked (the same branch, the opposite direction) ---- */
  const understockedRows = useMemo(() => {
    return rowsAtMonths
      .filter((r) => r.deficitPacks > 0 && matches(r.drugName))
      .map((r) => {
        const packsNeeded = r.deficitPacks;
        const valueNeeded = packsNeeded * r.packSize * r.unitValue;
        const sources = (allRows || [])
          .filter((o) => o.cleanName === r.cleanName && o.pharmacyId !== r.pharmacyId && o.usable)
          .map((o) => recompute(o, monthsToLeave))
          .filter((o) => o.certainExcessPacks > 0)
          .map((o) => ({
            pharmacyName: o.pharmacyName,
            available: o.certainExcessPacks,
          }))
          .sort((a, b) => b.available - a.available);
        return { row: r, packsNeeded, valueNeeded, sources };
      })
      .filter((x) => x.valueNeeded >= (Number(minValue) || 0));
  }, [rowsAtMonths, allRows, monthsToLeave, minValue, needle]); // eslint-disable-line react-hooks/exhaustive-deps

  const understockedSorted = useMemo(() => sortBy(understockedRows, sortUnderstocked, {
    name: (x) => x.row.drugName,
    months: (x) => x.row.monthsCover ?? -1,
    packs: (x) => x.row.packetsOnShelf,
    packsNeeded: (x) => x.packsNeeded,
    valueNeeded: (x) => x.valueNeeded,
    sources: (x) => x.sources.length,
  }), [understockedRows, sortUnderstocked]);

  const totalDisplayedValueNeeded = useMemo(
    () => understockedSorted.reduce((t, x) => t + x.valueNeeded, 0),
    [understockedSorted]
  );

  /* ---- tab 2: in stock, no usage ---- */
  const noUsageRows = useMemo(() => rowsAtMonths
    .filter((r) => r.qtyOnShelf > 0 && !(r.usage > 0) && matches(r.drugName)),
    [rowsAtMonths, needle]); // eslint-disable-line react-hooks/exhaustive-deps

  const noUsageSorted = useMemo(() => sortBy(noUsageRows, sortNoUsage, {
    name: (r) => r.drugName,
    packs: (r) => r.packetsOnShelf,
    value: (r) => r.totalValue,
  }), [noUsageRows, sortNoUsage]);

  /* ---- tab 3: usage, no stock ---- */
  const noStockRows = useMemo(() => rowsAtMonths
    .filter((r) => !(r.qtyOnShelf > 0) && r.usage > 0 && matches(r.drugName)),
    [rowsAtMonths, needle]); // eslint-disable-line react-hooks/exhaustive-deps

  const noStockSorted = useMemo(() => sortBy(noStockRows, sortNoStock, {
    name: (r) => r.drugName,
    usage: (r) => r.usage,
    deficit: (r) => r.deficitPacks || 0,
  }), [noStockRows, sortNoStock]);

  /* ---- tab 4: top 200 lines at this branch ---- */
  const top200 = useMemo(() => {
    const withUsage = rowsAtMonths.filter((r) => (r.monthly || []).some((m) => (m.qty || 0) > 0));
    const withMetrics = withUsage.map((r) => {
      const totalUnits = (r.monthly || []).reduce((t, m) => t + (m.qty || 0), 0);
      const totalPackets = Math.ceil((r.packSize > 0 ? totalUnits / r.packSize : totalUnits) - 1e-9);
      const totalValue = totalUnits * (r.unitValue || 0);
      return { row: r, totalPackets, totalValue };
    });
    const metricKey = topBasis === "value" ? "totalValue" : "totalPackets";
    return [...withMetrics].sort((a, b) => b[metricKey] - a[metricKey]).slice(0, 200);
  }, [rowsAtMonths, topBasis]);

  const topSorted = useMemo(() => sortBy(top200, sortTop, {
    name: (x) => x.row.drugName,
    metric: (x) => (topBasis === "value" ? x.totalValue : x.totalPackets),
    packets: (x) => x.totalPackets,
    value: (x) => x.totalValue,
  }), [top200, sortTop, topBasis]);

  /* ---- exports ---- */
  const doExport = () => {
    if (tab === "excess") {
      exportRows(`excess-stock-${pharmacyName || "branch"}.xlsx`, "Excess stock", pharmacyName, [
        { label: "Drug", value: (x) => x.row.drugName },
        { label: "Months held", value: (x) => (x.row.monthsCover == null ? "" : x.row.monthsCover.toFixed(1)) },
        { label: "Current packs", value: (x) => Math.round(x.row.packetsOnShelf) },
        { label: "Confidence", value: (x) => CONF_LABEL[x.row.confidence?.level] || "" },
        { label: "Packs available for transfer", value: (x) => x.transferPacks },
        { label: "Value of available transfer", value: (x) => Number(x.transferValue.toFixed(2)) },
        { label: "Possible recipients (needed)", value: (x) => x.recipients.length ? x.recipients.map((r) => `${displayName(r.pharmacyName, masked)} (${r.needed})`).join("; ") : "Transfer to warehouse" },
      ], excessSorted);
    } else if (tab === "understocked") {
      exportRows(`understocked-${pharmacyName || "branch"}.xlsx`, "Understocked", pharmacyName, [
        { label: "Drug", value: (x) => x.row.drugName },
        { label: "Months held", value: (x) => (x.row.monthsCover == null ? "" : x.row.monthsCover.toFixed(1)) },
        { label: "Current packs", value: (x) => Math.round(x.row.packetsOnShelf) },
        { label: "Confidence", value: (x) => CONF_LABEL[x.row.confidence?.level] || "" },
        { label: "Packs needed", value: (x) => x.packsNeeded },
        { label: "Value needed", value: (x) => Number(x.valueNeeded.toFixed(2)) },
        { label: "Possible sources (available)", value: (x) => x.sources.length ? x.sources.map((s) => `${displayName(s.pharmacyName, masked)} (${s.available})`).join("; ") : "None found" },
      ], understockedSorted);
    } else if (tab === "noUsage") {
      exportNoUsageRows(pharmacyName, noUsageSorted);
    } else if (tab === "noStock") {
      exportRows(`usage-no-stock-${pharmacyName || "branch"}.xlsx`, "Usage no stock", pharmacyName, [
        { label: "Drug", value: (r) => r.drugName },
        { label: "Avg monthly usage (units)", value: (r) => Number((r.usage || 0).toFixed(1)) },
        { label: "Packs short", value: (r) => r.deficitPacks || 0 },
      ], noStockSorted);
    } else {
      exportRows(`top-200-lines-${pharmacyName || "branch"}.xlsx`, "Top 200 lines", pharmacyName, [
        { label: "Drug", value: (x) => x.row.drugName },
        { label: "Total packets dispensed (3mo)", value: (x) => x.totalPackets },
        { label: "Total value dispensed (3mo)", value: (x) => Number(x.totalValue.toFixed(2)) },
      ], topSorted);
    }
  };

  const wrap = { background: "#fff", border: "1px solid #dfe3e8", borderRadius: 10, overflow: "hidden" };
  const barHeader = { padding: "10px 14px", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" };

  return (
    <div>
      {/* ---- tab bar ---- */}
      <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => { setTab(t.id); setExpanded(null); }}
            style={{
              padding: "8px 14px", fontWeight: 700, border: "1px solid #cbd5e1",
              borderBottom: tab === t.id ? "3px solid #2b7de9" : "1px solid #cbd5e1",
              borderRadius: "6px 6px 0 0", background: tab === t.id ? "#eef4fd" : "#f8fafc",
              color: tab === t.id ? "#1d4ed8" : "#334155", cursor: "pointer",
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={wrap}>
        <div style={barHeader}>
          <div style={{ fontWeight: 700 }}>{displayName(pharmacyName, masked)}</div>
          <div style={{ fontSize: "0.8em", color: "#94a3b8" }}
            title="The three months this branch's usage figures are actually averaged over. Falls back a month if the newest one is not yet fully reported.">
            Months available: {monthsLabel(usageWindow?.months || [])}
          </div>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search this branch's products…" style={{ padding: "6px 9px", minWidth: 220 }} />
          <span style={{ flex: 1 }} />
          <button onClick={doExport} style={{ padding: "7px 12px", fontWeight: 600 }}>Export to Excel</button>
        </div>

        {/* ---- sliders (excess + understocked tabs only) ---- */}
        {(tab === "excess" || tab === "understocked") && (
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #eef2f6", display: "flex", gap: 24, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "0.88em", color: "#334155" }}>
              Number of months to leave at branch
              <input type="range" min="0" max="9" step="1" value={monthsToLeave}
                onChange={(e) => setMonthsToLeave(Number(e.target.value))} style={{ width: 160 }} />
              <b>{monthsToLeave} month{monthsToLeave === 1 ? "" : "s"}</b>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "0.88em", color: "#334155" }}>
              {tab === "excess" ? "Only show if transfer value greater than" : "Only show if value needed greater than"}
              <input type="number" min="0" step="1" value={minValue}
                onChange={(e) => setMinValue(e.target.value)} style={{ width: 90, padding: "4px 6px" }} />
            </label>
            <span style={{ flex: 1 }} />
            <div style={{ fontSize: "0.9em", fontWeight: 700, color: "#1d4ed8", whiteSpace: "nowrap" }}>
              {tab === "excess"
                ? <>Total value of displayed possible transfers: {money(totalDisplayedTransferValue)}</>
                : <>Total value of displayed shortfalls: {money(totalDisplayedValueNeeded)}</>}
            </div>
          </div>
        )}

        {/* ---- radio buttons (tab 4 only) ---- */}
        {tab === "top200" && (
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #eef2f6", display: "flex", gap: 18, fontSize: "0.88em" }}>
            <label><input type="radio" checked={topBasis === "value"} onChange={() => setTopBasis("value")} /> Top in value</label>
            <label><input type="radio" checked={topBasis === "packets"} onChange={() => setTopBasis("packets")} /> Top in packet numbers</label>
          </div>
        )}

        <div style={{ maxHeight: 520, overflowY: "auto" }}>
          {/* ================= TAB 1 ================= */}
          {tab === "excess" && (
            <div>
              <div style={{
                display: "flex", position: "sticky", top: 0, background: "#f1f5f9",
                borderBottom: "1px solid #e5e7eb", fontSize: "0.82em", fontWeight: 700,
                color: "#334155", padding: "8px 10px", gap: 10,
              }}>
                {EXCESS_COLS.map((c) => {
                  const active = sortExcess.key === c.key;
                  return (
                    <div key={c.key}
                      onClick={() => c.sortable && setSortExcess((s) => (s.key === c.key ? { key: c.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: c.key, dir: "desc" }))}
                      style={{ flex: c.flex, textAlign: c.align || "left", cursor: c.sortable ? "pointer" : "default", userSelect: "none" }}>
                      {c.label}{active ? (sortExcess.dir === "asc" ? " \u25b2" : " \u25bc") : ""}
                    </div>
                  );
                })}
              </div>

              <div style={{ padding: "8px 10px" }}>
                {excessSorted.map((x, i) => {
                  const r = x.row;
                  const key = `${r.pharmacyId}|${r.cleanName}`;
                  const open = expanded === key;
                  return (
                    <div key={i} style={{
                      border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 8, overflow: "hidden",
                      background: open ? "#f8fafc" : "#fff",
                    }}>
                      <div onClick={() => setExpanded(open ? null : key)}
                        style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 10px", cursor: "pointer" }}>
                        <div style={{ flex: EXCESS_COLS[0].flex }}>{r.drugName}</div>
                        <div style={{ flex: EXCESS_COLS[1].flex }}>
                          <UsageDetail monthly={r.monthly} packSize={r.packSize} confidence={r.confidence} />
                        </div>
                        <div style={{ flex: EXCESS_COLS[2].flex, textAlign: "right" }}>{Math.round(r.packetsOnShelf)}</div>
                        <div style={{ flex: EXCESS_COLS[3].flex, textAlign: "right" }}>{r.monthsCover == null ? "—" : `${r.monthsCover.toFixed(1)} mo`}</div>
                        <div style={{ flex: EXCESS_COLS[4].flex, textAlign: "right", fontWeight: 700 }}>{x.transferPacks} pk</div>
                        <div style={{ flex: EXCESS_COLS[5].flex, textAlign: "right", fontWeight: 700 }}>{money(x.transferValue)}</div>
                        <div style={{ flex: EXCESS_COLS[6].flex }}>
                          {x.recipients.length ? (
                            x.recipients.map((rec, n) => (
                              <div key={n} style={{ whiteSpace: "nowrap" }}>
                                {displayName(rec.pharmacyName, masked)} — needs {rec.needed}
                              </div>
                            ))
                          ) : (
                            <span style={{ fontWeight: 700, color: "#6b4fa8" }}>Transfer to warehouse</span>
                          )}
                        </div>
                      </div>
                      {open && (
                        <div style={{ borderTop: "1px solid #eef2f6" }}>
                          <ItemsTable items={r.items} />
                        </div>
                      )}
                    </div>
                  );
                })}
                {excessSorted.length === 0 && (
                  <div style={{ color: "#64748b", textAlign: "center", padding: "16px 0" }}>
                    Nothing spare at this branch on the current settings.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ================= TAB 1b ================= */}
          {tab === "understocked" && (
            <div>
              <div style={{
                display: "flex", position: "sticky", top: 0, background: "#f1f5f9",
                borderBottom: "1px solid #e5e7eb", fontSize: "0.82em", fontWeight: 700,
                color: "#334155", padding: "8px 10px", gap: 10,
              }}>
                {UNDERSTOCKED_COLS.map((c) => {
                  const active = sortUnderstocked.key === c.key;
                  return (
                    <div key={c.key}
                      onClick={() => c.sortable && setSortUnderstocked((s) => (s.key === c.key ? { key: c.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: c.key, dir: "desc" }))}
                      style={{ flex: c.flex, textAlign: c.align || "left", cursor: c.sortable ? "pointer" : "default", userSelect: "none" }}>
                      {c.label}{active ? (sortUnderstocked.dir === "asc" ? " \u25b2" : " \u25bc") : ""}
                    </div>
                  );
                })}
              </div>

              <div style={{ padding: "8px 10px" }}>
                {understockedSorted.map((x, i) => {
                  const r = x.row;
                  const key = `${r.pharmacyId}|${r.cleanName}`;
                  const open = expanded === key;
                  return (
                    <div key={i} style={{
                      border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 8, overflow: "hidden",
                      background: open ? "#f8fafc" : "#fff",
                    }}>
                      <div onClick={() => setExpanded(open ? null : key)}
                        style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 10px", cursor: "pointer" }}>
                        <div style={{ flex: UNDERSTOCKED_COLS[0].flex }}>{r.drugName}</div>
                        <div style={{ flex: UNDERSTOCKED_COLS[1].flex }}>
                          <UsageDetail monthly={r.monthly} packSize={r.packSize} confidence={r.confidence} />
                        </div>
                        <div style={{ flex: UNDERSTOCKED_COLS[2].flex, textAlign: "right" }}>{Math.round(r.packetsOnShelf)}</div>
                        <div style={{ flex: UNDERSTOCKED_COLS[3].flex, textAlign: "right" }}>{r.monthsCover == null ? "—" : `${r.monthsCover.toFixed(1)} mo`}</div>
                        <div style={{ flex: UNDERSTOCKED_COLS[4].flex, textAlign: "right", fontWeight: 700, color: "#a03030" }}>{x.packsNeeded} pk</div>
                        <div style={{ flex: UNDERSTOCKED_COLS[5].flex, textAlign: "right", fontWeight: 700, color: "#a03030" }}>{money(x.valueNeeded)}</div>
                        <div style={{ flex: UNDERSTOCKED_COLS[6].flex }}>
                          {x.sources.length ? (
                            x.sources.map((s, n) => (
                              <div key={n} style={{ whiteSpace: "nowrap" }}>
                                {displayName(s.pharmacyName, masked)} — has {s.available}
                              </div>
                            ))
                          ) : (
                            <span style={{ fontWeight: 700, color: "#6b4fa8" }}>No branch has spare stock</span>
                          )}
                        </div>
                      </div>
                      {open && (
                        <div style={{ borderTop: "1px solid #eef2f6" }}>
                          <ItemsTable items={r.items} />
                        </div>
                      )}
                    </div>
                  );
                })}
                {understockedSorted.length === 0 && (
                  <div style={{ color: "#64748b", textAlign: "center", padding: "16px 0" }}>
                    Nothing understocked at this branch on the current settings.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ================= TAB 2 ================= */}
          {tab === "noUsage" && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88em" }}>
              <thead style={{ position: "sticky", top: 0, background: "#f1f5f9" }}>
                <tr>
                  <SortTh label="Drug" colKey="name" sort={sortNoUsage} setSort={setSortNoUsage} />
                  <SortTh label="Current packs" colKey="packs" sort={sortNoUsage} setSort={setSortNoUsage} align="right" />
                  <SortTh label="Value" colKey="value" sort={sortNoUsage} setSort={setSortNoUsage} align="right" />
                  <th style={th} title="Soonest expiry on the shelf for this drug">Expiry</th>
                </tr>
              </thead>
              <tbody>
                {noUsageSorted.map((r, i) => {
                  const key = `${r.pharmacyId}|${r.cleanName}`;
                  const open = expanded === key;
                  const soonestExpiry = r.items && r.items[0] && r.items[0].expiryDate
                    ? String(r.items[0].expiryDate).slice(0, 10) : "-";
                  return (
                    <React.Fragment key={i}>
                      <tr onClick={() => setExpanded(open ? null : key)} style={{ cursor: "pointer", background: open ? "#f8fafc" : "#fff" }}>
                        <td style={td}>{r.drugName}</td>
                        <td style={{ ...td, textAlign: "right" }}>{Math.round(r.packetsOnShelf)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{money(r.totalValue)}</td>
                        <td style={td}>{soonestExpiry}</td>
                      </tr>
                      {open && (
                        <tr><td colSpan={4} style={{ padding: 0, background: "#fbfcfd" }}><ItemsTable items={r.items} /></td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {noUsageSorted.length === 0 && (
                  <tr><td colSpan={4} style={{ ...td, color: "#64748b", textAlign: "center" }}>Nothing on the shelf with zero or missing usage.</td></tr>
                )}
              </tbody>
            </table>
          )}

          {/* ================= TAB 3 ================= */}
          {tab === "noStock" && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88em" }}>
              <thead style={{ position: "sticky", top: 0, background: "#f1f5f9" }}>
                <tr>
                  <SortTh label="Drug" colKey="name" sort={sortNoStock} setSort={setSortNoStock} />
                  <th style={th}>Usage over 3 months</th>
                  <SortTh label="Avg monthly usage" colKey="usage" sort={sortNoStock} setSort={setSortNoStock} align="right" />
                  <SortTh label="Packs short" colKey="deficit" sort={sortNoStock} setSort={setSortNoStock} align="right" />
                </tr>
              </thead>
              <tbody>
                {noStockSorted.map((r, i) => (
                  <tr key={i}>
                    <td style={td}>{r.drugName}</td>
                    <td style={td}><UsageDetail monthly={r.monthly} packSize={r.packSize} confidence={r.confidence} /></td>
                    <td style={{ ...td, textAlign: "right" }}>{(r.usage || 0).toFixed(1)}</td>
                    <td style={{ ...td, textAlign: "right", color: r.deficitPacks > 0 ? "#a03030" : "#94a3b8" }}>{r.deficitPacks || 0}</td>
                  </tr>
                ))}
                {noStockSorted.length === 0 && (
                  <tr><td colSpan={4} style={{ ...td, color: "#64748b", textAlign: "center" }}>Nothing with usage but no stock at this branch.</td></tr>
                )}
              </tbody>
            </table>
          )}

          {/* ================= TAB 4 ================= */}
          {tab === "top200" && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88em" }}>
              <thead style={{ position: "sticky", top: 0, background: "#f1f5f9" }}>
                <tr>
                  <SortTh label="Drug" colKey="name" sort={sortTop} setSort={setSortTop} />
                  <SortTh label="Total packets dispensed" colKey="packets" sort={sortTop} setSort={setSortTop} align="right" />
                  <SortTh label="Total value dispensed" colKey="value" sort={sortTop} setSort={setSortTop} align="right" />
                </tr>
              </thead>
              <tbody>
                {topSorted.map((x, i) => (
                  <tr key={i}>
                    <td style={td}>{x.row.drugName}</td>
                    <td style={{ ...td, textAlign: "right" }}>{x.totalPackets}</td>
                    <td style={{ ...td, textAlign: "right" }}>{money(x.totalValue)}</td>
                  </tr>
                ))}
                {topSorted.length === 0 && (
                  <tr><td colSpan={3} style={{ ...td, color: "#64748b", textAlign: "center" }}>No dispensing recorded for this branch in the last three months.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}